import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Server, Globe, Shield, Zap, Code, Users, Coffee, X, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PricingSection from './PricingSection';
import Header from './Header';
import { useHashScroll } from '../hooks/useHashScroll';
import { EXTERNAL_URLS } from '../config';

// Componente de botão personalizado com efeito hover
const Button = ({ children, primary, className, onClick }) => {
  return (
    <button 
      onClick={onClick}
      className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
        primary 
          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-purple-500/40 focus:ring-purple-500' 
          : 'bg-white text-gray-800 hover:shadow-md focus:ring-gray-300'
      } ${className}`}
    >
      {children}
    </button>
  );
};

// Componente para as estatísticas com animação de contagem
const StatCounter = ({ end, label, suffix = "", duration = 2000, icon: Icon, description, realTime = false }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      
      const easeOutQuad = t => t * (2 - t);
      const progressRatio = Math.min(easeOutQuad(progress / duration), 1);
      
      setCount(Math.floor(progressRatio * end));
      
      if (progress < duration) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [end, duration]);
  
  return (
    <div className="text-center group">
      <div className="bg-white bg-opacity-5 backdrop-filter backdrop-blur-lg rounded-2xl p-8 border border-white border-opacity-10 hover:border-purple-400/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg shadow-purple-500/30 group-hover:shadow-xl group-hover:shadow-purple-500/50 transition-all duration-300">
          <Icon size={28} className="text-white" />
        </div>
        <div className="text-5xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors duration-300">
          {count}{suffix}
        </div>
        <h3 className="text-xl font-semibold text-purple-200 mb-2 group-hover:text-purple-100 transition-colors duration-300">
          {label}
        </h3>
        {realTime && (
          <div className="flex items-center justify-center gap-2 text-xs text-emerald-300 mb-3">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="uppercase tracking-wide font-semibold">Tempo real</span>
          </div>
        )}
        <p className="text-gray-300 text-sm leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
          {description}
        </p>
      </div>
    </div>
  );
};

// Componente para as características/recursos com ícones
const FeatureCard = ({ icon: Icon, title, description }) => {
  return (
    <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20 border border-gray-700 border-opacity-30">
      <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4 shadow-lg shadow-purple-500/30">
        <Icon size={24} className="text-white" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </div>
  );
};

// Componente de depoimento
const Testimonial = ({ name, role, text }) => {
  return (
    <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg rounded-2xl p-8 border border-gray-200 shadow-xl transition-transform duration-300 hover:scale-105 hover:shadow-2xl flex flex-col h-full">
      <div className="flex items-center mb-5">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full p-1 shadow-md">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
            <Coffee size={24} className="text-purple-500" />
          </div>
        </div>
        <div className="ml-5">
          <h4 className="text-gray-900 font-bold text-lg leading-tight">{name}</h4>
          <p className="text-purple-600 text-sm font-medium">{role}</p>
        </div>
      </div>
      <p className="text-gray-800 italic text-base flex-1">"{text}"</p>
    </div>
  );
};

// Link acessível personalizado
const FooterLink = ({ children, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="text-gray-400 hover:text-purple-400 transition-colors text-left"
    >
      {children}
    </button>
  );
};

// Ícone de rede social acessível
const SocialIcon = ({ icon, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="text-gray-400 hover:text-purple-400 transition-colors"
      aria-label="Link de rede social"
    >
      {icon}
    </button>
  );
};

// Item do menu mobile
const MobileMenuItem = ({ icon: Icon, label, onClick, isActive = false }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
        isActive 
          ? 'bg-purple-600/20 text-white' 
          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
      }`}
    >
      {Icon && <Icon size={18} className={isActive ? 'text-purple-400' : ''} />}
      <span className="font-medium">{label}</span>
    </button>
  );
};

// Componente principal da landing page
const HostulLandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMobileSection, setActiveMobileSection] = useState('');
  const [scrollPosition, setScrollPosition] = useState(0);
  const mobileMenuRef = useRef(null);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const navigate = useNavigate();

  // Usar o hook para tratar âncoras na URL
  useHashScroll();

  // Controlar o comportamento do menu mobile e o scroll
  useEffect(() => {
    if (isMenuOpen) {
      // Salvar a posição atual do scroll
      setScrollPosition(window.pageYOffset);
      // Adicionar classe ao body para impedir scrolling
      document.body.style.overflow = 'hidden';
      // Fixar o body na posição atual
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';
    } else {
      // Restaurar scrolling e posição
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      // Restaurar a posição do scroll
      if (scrollPosition) {
        window.scrollTo(0, scrollPosition);
      }
    }
    
    // Fechar o menu ao clicar fora dele
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    // Adicionar listener para fechar menu ao pressionar ESC
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen, scrollPosition]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    }
    if (isServicesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isServicesOpen]);

  // Funções para lidar com cliques
  const handleLinkClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setActiveMobileSection(sectionId);
    }
    setIsMenuOpen(false);
  };
  
  const handleSocialClick = (network) => {
    console.log(`Clicou na rede social: ${network}`);
  };

  // Função para redirecionar para a página de autenticação
  const navigateToAuth = (mode) => {
    localStorage.setItem('auth_mode', mode);
    window.location.href = mode === 'register' ? EXTERNAL_URLS.register : EXTERNAL_URLS.authBase;
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white overflow-x-hidden">
      <Header />
      
      {/* Menu mobile (overlay) */}
      {isMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-gray-900/95 backdrop-filter backdrop-blur-lg z-50 flex flex-col overscroll-none"
          ref={mobileMenuRef}
          style={{ top: `${scrollPosition}px` }}
        >
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
            <div className="flex items-center">
              <div className="mr-2 text-purple-500">
                <Server size={24} />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Hostul
              </span>
            </div>
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
              aria-label="Fechar menu"
            >
              <X size={20} className="text-white" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-2 mb-8">
              <MobileMenuItem 
                icon={Zap} 
                label="Recursos" 
                onClick={() => handleLinkClick('recursos')}
                isActive={activeMobileSection === 'recursos'} 
              />
              <MobileMenuItem 
                icon={Globe} 
                label="Planos" 
                onClick={() => handleLinkClick('planos')}
                isActive={activeMobileSection === 'planos'} 
              />
              <MobileMenuItem 
                icon={Users} 
                label="Depoimentos" 
                onClick={() => handleLinkClick('depoimentos')}
                isActive={activeMobileSection === 'depoimentos'} 
              />
              <MobileMenuItem 
                icon={Code} 
                label="Contato" 
                onClick={() => handleLinkClick('contato')}
                isActive={activeMobileSection === 'contato'} 
              />
              <MobileMenuItem 
                icon={Shield} 
                label="Serviços" 
                onClick={() => setIsServicesOpen((v) => !v)}
                isActive={activeMobileSection === 'servicos'} 
              />
              {isServicesOpen && (
                <div className="bg-gray-800 rounded-lg p-3 mb-2 animate-fade-in">
                  <div className="font-semibold text-purple-400 mb-2">Nossos Serviços</div>
                  <button className="flex items-center gap-2 text-left text-white hover:text-purple-400 transition-colors p-2 rounded-lg hover:bg-gray-700">
                    <Server size={18} /> Hospedagem de Sites
                  </button>
                  <button className="flex items-center gap-2 text-left text-white hover:text-purple-400 transition-colors p-2 rounded-lg hover:bg-gray-700">
                    <Globe size={18} /> Hospedagem WordPress
                  </button>
                  <button className="flex items-center gap-2 text-left text-white hover:text-purple-400 transition-colors p-2 rounded-lg hover:bg-gray-700">
                    <Shield size={18} /> Email Profissional
                  </button>
                  <button className="flex items-center gap-2 text-left text-white hover:text-purple-400 transition-colors p-2 rounded-lg hover:bg-gray-700">
                    <Zap size={18} /> Hospedagem Cloud
                  </button>
                  <button className="flex items-center gap-2 text-left text-white hover:text-purple-400 transition-colors p-2 rounded-lg hover:bg-gray-700">
                    <Code size={18} /> Criador de Sites Inteligente
                  </button>
                  <button className="flex items-center gap-2 text-left text-white hover:text-purple-400 transition-colors p-2 rounded-lg hover:bg-gray-700">
                    <Users size={18} /> Registrar Domínio
                  </button>
                  <div className="mt-2 text-xs text-gray-400">Descubra soluções exclusivas para seu negócio!</div>
                </div>
              )}
              <MobileMenuItem 
                icon={Code} 
                label="Engenharia de Sites" 
                onClick={() => { navigate('/engenharia'); setIsMenuOpen(false); }}
                isActive={activeMobileSection === 'engenharia'} 
              />
            </div>
            
            <div className="space-y-3">
              <button 
                onClick={() => navigateToAuth('login')}
                className="w-full py-3 px-4 rounded-lg border border-gray-600 text-white font-medium hover:bg-gray-800 transition-colors"
              >
                Entrar
              </button>
              <button 
                onClick={() => navigateToAuth('register')}
                className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium hover:shadow-lg hover:shadow-purple-500/20 transition-all"
              >
                Registrar
              </button>
            </div>
            
            <div className="mt-8 pt-6 border-t border-gray-800">
              <div className="flex justify-center space-x-6">
                <SocialIcon 
                  icon={
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"></path>
                    </svg>
                  } 
                  onClick={() => handleSocialClick('facebook')}
                />
                <SocialIcon 
                  icon={
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm-1-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm8 7h-2v-3c0-.55-.45-1-1-1s-1 .45-1 1v3h-2v-6h2v1.1c.52-.62 1.45-1.1 2.5-1.1 1.93 0 3.5 1.57 3.5 3.5v2.5z"></path>
                    </svg>
                  } 
                  onClick={() => handleSocialClick('linkedin')}
                />
                <SocialIcon 
                  icon={
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"></path>
                    </svg>
                  } 
                  onClick={() => handleSocialClick('twitter')}
                />
              </div>
              <div className="mt-6 text-center text-sm text-gray-400">
                <p>&copy; 2026 Hostul. Todos os direitos reservados.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Hero section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 relative overflow-hidden">
        {/* Efeito de partículas/grid no fundo */}
        <div className="absolute inset-0 z-0 opacity-20 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(#9f7aea_1px,transparent_1px)] [background-size:20px_20px]"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Hospedagem de Sites Confiável, Rápida e Segura
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed">
              Infraestrutura de última geração para o seu site ou aplicação.
              Hospedagem que cresce junto com o seu negócio.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button primary className="text-lg" onClick={() => handleLinkClick('plano-basico')}>
                Comece Grátis
              </Button>
              <Button className="text-lg" onClick={() => handleLinkClick('planos')}>
                Ver Planos
              </Button>
            </div>
            
            <div className="mt-16 flex flex-col items-center">
              <button 
                onClick={() => handleLinkClick('estatisticas')} 
                className="flex flex-col items-center text-gray-400 hover:text-white transition-colors animate-bounce bg-transparent border-none"
                aria-label="Saiba mais"
              >
                <span className="mb-2">Saiba mais</span>
                <ChevronDown size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Estatísticas */}
      <section id="estatisticas" className="py-24 bg-gradient-to-br from-gray-900 via-purple-900/30 to-indigo-900/30 overflow-hidden relative">
        {/* Efeito de fundo animado */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(147,51,234,0.15),transparent_40%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(99,102,241,0.12),transparent_40%)]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_25%,rgba(147,51,234,0.03)_50%,transparent_75%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(#9f7aea_1px,transparent_1px)] [background-size:30px_30px] opacity-5"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Infraestrutura de Confiança
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Nossa plataforma oferece a tecnologia e suporte necessários para seu projeto decolar
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StatCounter 
              end={99.9} 
              suffix="%" 
              label="Uptime Garantido" 
              icon={Zap}
              description="Disponibilidade ininterrupta com monitoramento 24/7 e compensação automática em caso de indisponibilidade"
            />
            <StatCounter 
              end={40} 
              label="Sites no ar" 
              icon={Users}
              description="Sites ativos hospedados agora na nossa infraestrutura"
              realTime
            />
            <StatCounter 
              end={24} 
              suffix="/7" 
              label="Suporte Especializado" 
              icon={Coffee}
              description="Equipe técnica brasileira disponível a qualquer momento para resolver suas dúvidas e problemas"
            />
          </div>
          
          {/* Seção adicional de benefícios */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-3">
                <Shield size={24} className="text-purple-400 mx-auto" />
              </div>
              <h4 className="text-white font-semibold mb-2">SSL Gratuito</h4>
              <p className="text-gray-300 text-sm">Certificados SSL incluídos em todos os planos</p>
            </div>
            <div className="text-center">
              <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-3">
                <Server size={24} className="text-purple-400 mx-auto" />
              </div>
              <h4 className="text-white font-semibold mb-2">Servidores NVMe</h4>
              <p className="text-gray-300 text-sm">Tecnologia de armazenamento de alta velocidade</p>
            </div>
            <div className="text-center">
              <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-3">
                <Globe size={24} className="text-purple-400 mx-auto" />
              </div>
              <h4 className="text-white font-semibold mb-2">CDN Global</h4>
              <p className="text-gray-300 text-sm">Distribuição de conteúdo em todo o mundo</p>
            </div>
            <div className="text-center">
              <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-3">
                <Code size={24} className="text-purple-400 mx-auto" />
              </div>
              <h4 className="text-white font-semibold mb-2">Migração em Planos Elegíveis</h4>
              <p className="text-gray-300 text-sm">Incluída nos planos Profissional e Hostul Max</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Recursos */}
      <section id="recursos" className="py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Recursos Completos para Seu Site
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Nossa infraestrutura foi projetada para oferecer o melhor desempenho, segurança e facilidade de uso.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={Zap}
              title="Desempenho Ultrarrápido"
              description="Servidores NVMe de alta velocidade e CDN global para carregamento rápido em qualquer lugar do mundo."
            />
            <FeatureCard
              icon={Shield}
              title="Segurança Avançada"
              description="Proteção contra DDoS, firewall, SSL gratuito e backups semanais ou diários, conforme o plano."
            />
            <FeatureCard
              icon={Globe}
              title="Sites Conforme o Plano"
              description="Hospede 1 site no Básico, até 50 no Profissional ou até 100 no Hostul Max."
            />
            <FeatureCard
              icon={Code}
              title="Painel Intuitivo"
              description="Interface amigável para gerenciar seus sites, domínios, e-mails e bancos de dados."
            />
            <FeatureCard
              icon={Users}
              title="Suporte Especializado"
              description="Equipe técnica disponível 24/7 para ajudar com qualquer problema ou dúvida."
            />
            <FeatureCard
              icon={Coffee}
              title="Experiência Tranquila"
              description="Instalação de CMS com um clique e migração gratuita nos planos Profissional e Hostul Max."
            />
          </div>
        </div>
      </section>
      
      {/* Planos de preço */}
      <PricingSection />
      
      {/* Depoimentos */}
      <section id="depoimentos" className="py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                O que Nossos Clientes Dizem
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Milhares de clientes confiam na Hostul para hospedar seus projetos.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Testimonial
              name="Pedro Silva"
              role="Desenvolvedor Web"
              text="Mudei para a Hostul há 6 meses e foi a melhor decisão que tomei. O suporte é incrível e a velocidade dos servidores supera todas as minhas expectativas."
            />
            <Testimonial
              name="Ana Rodrigues"
              role="Proprietária de E-commerce"
              text="Minha loja online nunca teve um desempenho tão bom. Durante a Black Friday, tivemos um pico de tráfego e o site continuou funcionando perfeitamente."
            />
            <Testimonial
              name="Carlos Mendes"
              role="Agência Digital"
              text="Gerenciamos mais de 30 sites de clientes na Hostul e nunca tivemos problemas. O painel de controle é intuitivo e facilita muito nosso trabalho."
            />
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-purple-900 to-indigo-900 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Pronto para Elevar seu Site a Outro Nível?
            </h2>
            <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto">
              Junte-se a milhares de clientes satisfeitos e experimente o serviço de hospedagem com o melhor custo-benefício do mercado.
            </p>
            <Button
              primary
              className="text-lg px-10 py-4"
              onClick={() => document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Escolher Meu Plano
            </Button>
            <p className="mt-6 text-gray-300">
              Teste grátis por 14 dias, uma vez por conta elegível e sem cartão
            </p>
          </div>
        </div>
      </section>
      
      {/* FAQ */}
      <section className="py-24 overflow-hidden">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Perguntas Frequentes
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Tire suas dúvidas sobre nossos serviços de hospedagem
            </p>
          </div>
          <FAQAccordion />
        </div>
      </section>
      
      {/* Footer */}
      <footer id="contato" className="bg-gray-900 py-16 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center mb-6">
                <div className="mr-2 text-purple-500">
                  <Server size={24} />
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                  Hostul
                </span>
              </div>
              <p className="text-gray-400 mb-6">
                Hospedagem de qualidade com foco em desempenho, segurança e facilidade de uso.
              </p>
              <div className="flex space-x-4">
                <SocialIcon 
                  icon={
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"></path>
                    </svg>
                  } 
                  onClick={() => handleSocialClick('facebook')}
                />
                <SocialIcon 
                  icon={
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm-1-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm8 7h-2v-3c0-.55-.45-1-1-1s-1 .45-1 1v3h-2v-6h2v1.1c.52-.62 1.45-1.1 2.5-1.1 1.93 0 3.5 1.57 3.5 3.5v2.5z"></path>
                    </svg>
                  } 
                  onClick={() => handleSocialClick('linkedin')}
                />
                <SocialIcon 
                  icon={
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"></path>
                    </svg>
                  } 
                  onClick={() => handleSocialClick('twitter')}
                />
              </div>
            </div>
            
            <div>
              <h3 className="text-white font-semibold text-lg mb-6">Empresa</h3>
              <ul className="space-y-4">
                <li><FooterLink onClick={() => console.log('Sobre nós')}>Sobre nós</FooterLink></li>
                <li><FooterLink onClick={() => console.log('Blog')}>Blog</FooterLink></li>
                <li><FooterLink onClick={() => console.log('Carreiras')}>Carreiras</FooterLink></li>
                <li><FooterLink onClick={() => console.log('Parceiros')}>Parceiros</FooterLink></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold text-lg mb-6">Suporte</h3>
              <ul className="space-y-4">
                <li><FooterLink onClick={() => console.log('Central de Ajuda')}>Central de Ajuda</FooterLink></li>
                <li><FooterLink onClick={() => console.log('Base de Conhecimento')}>Base de Conhecimento</FooterLink></li>
                <li><FooterLink onClick={() => console.log('Status do Sistema')}>Status do Sistema</FooterLink></li>
                <li><FooterLink onClick={() => console.log('Contato')}>Contato</FooterLink></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold text-lg mb-6">Recursos</h3>
              <ul className="space-y-4">
                <li><FooterLink onClick={() => console.log('Documentação')}>Documentação</FooterLink></li>
                <li><FooterLink onClick={() => console.log('API')}>API</FooterLink></li>
                <li><FooterLink onClick={() => console.log('Parcerias')}>Parcerias</FooterLink></li>
                <li><FooterLink onClick={() => console.log('Afiliados')}>Afiliados</FooterLink></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex flex-wrap justify-center gap-6 text-sm mb-4">
              <a href="/legal/termosdeservico/#termos-politicas" className="text-gray-400 hover:text-purple-400 transition-colors">Termos de Serviço</a>
              <a href="/legal/politicadeprivacidade/#termos-politicas" className="text-gray-400 hover:text-purple-400 transition-colors">Política de Privacidade</a>
            </div>
            <p className="text-center text-gray-500">&copy; 2026 Hostul. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Componente FAQ Accordion
const FAQAccordion = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      q: 'Como funciona a migração do meu site atual?',
      a: 'A migração gratuita está incluída nos planos Profissional e Hostul Max. Nossa equipe orienta a transferência dos dados e a configuração do site; condições e disponibilidade são confirmadas pelo suporte antes do início do processo.'
    },
    {
      q: 'Quais são os diferenciais da Hostul em relação aos concorrentes?',
      a: 'Nossa infraestrutura utiliza servidores NVMe de alta velocidade, CDN global, certificados SSL gratuitos e suporte técnico brasileiro 24/7. Os backups são semanais no plano Básico e diários nos planos Profissional e Hostul Max.'
    },
    {
      q: 'Posso hospedar múltiplos sites em um único plano?',
      a: 'A quantidade depende do plano: o Básico hospeda 1 site, o Profissional até 50 sites e o Hostul Max até 100 sites. Todos podem ser gerenciados pelo painel dentro do limite contratado.'
    },
    {
      q: 'Como funciona o suporte técnico 24/7?',
      a: 'Nossa equipe técnica brasileira está disponível 24 horas por dia, 7 dias por semana, através de chat ao vivo, e-mail e telefone. Respondemos rapidamente a qualquer dúvida ou problema, garantindo que seu site esteja sempre funcionando perfeitamente.'
    },
    {
      q: 'Vocês oferecem certificados SSL gratuitos?',
      a: 'Sim! Todos os nossos planos incluem certificados SSL gratuitos, garantindo que seu site tenha conexão segura (HTTPS) e transmita confiança aos seus visitantes. A instalação e renovação são automáticas.'
    },
    {
      q: 'Como funciona o backup automático?',
      a: 'A frequência depende do plano contratado: backups semanais no Básico e diários no Profissional e Hostul Max. As opções de retenção e restauração disponíveis são apresentadas no painel.'
    },
    {
      q: 'Posso escalar meu plano conforme meu site cresce?',
      a: 'Com certeza! Nossa infraestrutura é escalável e você pode fazer upgrade do seu plano a qualquer momento, sem interrupção do serviço. Conforme seu site cresce em tráfego e recursos, você pode facilmente migrar para planos mais robustos.'
    },
    {
      q: 'Vocês oferecem domínios?',
      a: 'Sim! Oferecemos registro de domínios com preços competitivos e gerenciamento integrado ao painel de controle. Você pode registrar novos domínios ou transferir domínios existentes de forma simples e rápida.'
    }
  ];

  return (
    <div className="flex flex-col items-center gap-4">
      {faqs.map((faq, idx) => (
        <div key={idx} className="w-full max-w-2xl bg-white/10 border border-gray-700 rounded-xl overflow-hidden shadow-lg">
          <button
            className="w-full flex justify-between items-center px-6 py-5 text-left focus:outline-none hover:bg-purple-900/20 transition-colors"
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            aria-expanded={openIndex === idx}
          >
            <span className="text-lg font-semibold text-white">{faq.q}</span>
            {openIndex === idx ? <ChevronUp size={22} className="text-purple-400" /> : <ChevronDown size={22} className="text-purple-400" />}
          </button>
          {openIndex === idx && (
            <div className="px-6 pb-6 text-gray-300 animate-fade-in text-lg leading-relaxed">
              {faq.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default HostulLandingPage;
